import {ActionEvent, Controller} from '@hotwired/stimulus';
import {arrow, autoUpdate, computePosition, flip, offset, Placement, shift} from '@floating-ui/dom';
import {addEventListener, createHTMLElementFromTemplate} from 'app/utils/dom';
import {appendToRoot, Root} from 'app/utils/element-placer';
import {Typed} from "app/stimulus/typed-stimulus";
import {AsStimulusController} from "app/stimulus/as-stimulus-controller";
import {ResolvableController} from "app/stimulus/resolvable-controller";
import overlayStack from "app/service/overlay-stack";
import {Component} from "@symfony/ux-live-component";
import {onLiveComponentRender} from "app/stimulus/live-component-render";
import {
    addIdReference,
    closeWithNestedOverlayAfterAction,
    ensurePanelId,
    findCloseOnClickElement,
    findInitialFocus,
    focusWithoutScroll,
    isElementVisible,
    isFocusInside,
    isFocusInsideOrLost,
    isInsideOverlayTree,
    isKeyboardActivation,
    isPopoverOpen,
    isToggleCloseEvent,
    OverlayClosedDetail,
    removeIdReference,
    resolveLiveComponent,
    setPanelOwner,
    triggersLiveAction,
    waitForLiveRequest,
} from "app/stimulus/overlay-panel";

const controllerName = 'popover';

const closeOnClickAttribute = 'data-popover-close-on-click';

const hoverHideDelay = 50;

/**
 * Popover rendered from the `template` target into a native `popover="manual"` element in the top layer.
 * Shown on hover/focus, or on click with `dropdownBehaviour`. An outside click (also a tap outside a hover
 * popover on touch screens) closes it. Escape closes the topmost overlay. A close-on-click element that
 * triggers a Live action keeps the popover open, with the element's loader visible, until the Live request
 * ends; the overlay whose panel holds the trigger then closes too (its JS-appended panel is stale).
 *
 * A hover popover is a tooltip (`role="tooltip"` unless the panel sets its own role) and describes its
 * trigger through `aria-describedby` while it is shown.
 *
 * A click popover is a non-modal dialog (`role="dialog"` unless the panel sets its own role): opened from the
 * keyboard it moves the focus to its `[autofocus]` or first focusable element, and closing it while the focus
 * is inside returns the focus to the trigger.
 */
@AsStimulusController(controllerName)
export default class PopoverController extends Typed(
    ResolvableController(Controller<HTMLElement>, controllerName), {
        values: {
            data: String,
            removeOnHide: Boolean,
            root: {type: String, default: 'body'},
            dropdownBehaviour: Boolean,
            placement: String,
            closeOnEvents: Array<string>,
        },
        targets: {
            template: HTMLTemplateElement,
        },
        classes: [
            'caretTop',
            'caretBottom',
            'isOpen',
        ] as const,
    }
)
{
    private dataElement: HTMLElement | null = null;
    private dataElementListeners: AbortController | null = null;
    private isPopoverFocused: boolean = false;
    private isPopoverVisible: boolean = false;
    private hideTimeout: ReturnType<typeof setTimeout> | null = null;
    private stopAutoUpdate: (() => void) | null = null;
    private liveComponent: Component | null = null;
    private openCount: number = 0;
    private describedById: string | null = null;
    private describedElement: HTMLElement | null = null;
    private removeRenderListener: (() => void) | null = null;

    public connect(): void {
        if (!this.isDropdownBehaviour()) {
            return;
        }
        this.syncTriggerAria();
        // A Live render puts the server attributes of the trigger back, which may lack these two.
        this.removeRenderListener = onLiveComponentRender(this.element, (): void => this.syncTriggerAria());
    }

    public disconnect(): void {
        this.removeRenderListener?.();
        this.removeRenderListener = null;
        this.clearHideTimeout();
        this.hideImmediately();
        this.destroyDataElement();
    }

    public isOpened(): boolean {
        return this.isPopoverVisible;
    }

    /**
     * The panel element, or null before the first show (and after a hide with `removeOnHide`).
     */
    public getPopover(): HTMLElement | null {
        return this.dataElement;
    }

    public async open(): Promise<void> {
        await this.showPanel(false);
    }

    public async close(): Promise<void> {
        this.clearHideTimeout();
        this.hideImmediately();
    }

    public async toggle(event: ActionEvent | null = null): Promise<void> {
        if (!this.isDropdownBehaviour()) {
            return;
        }
        // With `root: 'toggler'` the panel lives inside the trigger, so clicks inside it bubble to the trigger.
        if (this.dataElement !== null && event !== null && event.target instanceof Node && this.dataElement.contains(event.target)) {
            return;
        }
        if (this.isPopoverVisible) {
            this.clearHideTimeout();
            this.hideImmediately(isKeyboardActivation(event));
        } else {
            await this.showPanel(isKeyboardActivation(event));
        }
    }

    public async show(): Promise<void> {
        await this.showPanel(false);
    }

    private async showPanel(moveFocusInside: boolean): Promise<void> {
        this.clearHideTimeout();
        if (this.isPopoverVisible) {
            return;
        }
        const popover = this.getDataElement();
        this.dispatch('opening');
        popover.style.visibility = 'hidden';
        popover.style.position = 'fixed';
        popover.style.left = '0px';
        popover.style.top = '0px';
        if (!isPopoverOpen(popover)) {
            popover.showPopover();
        }
        this.isPopoverVisible = true;
        const openCount = ++this.openCount;
        overlayStack.register(this);
        if (this.isDropdownBehaviour()) {
            this.element.setAttribute('aria-expanded', 'true');
        } else {
            this.describedById = ensurePanelId(popover, controllerName);
            this.describedElement = this.findDescribedElement();
            addIdReference(this.describedElement, 'aria-describedby', this.describedById);
        }
        popover.removeAttribute('aria-hidden');
        this.element.classList.add(this.isOpenClass);
        popover.classList.add(this.isOpenClass);
        await this.computePosition();
        // A hide (and possibly another show) during the measurement makes this show stale.
        if (!this.isPopoverVisible || this.dataElement !== popover || openCount !== this.openCount) {
            return;
        }
        popover.style.visibility = '';
        this.stopPositionUpdates();
        this.stopAutoUpdate = autoUpdate(this.element, popover, (): void => {
            this.computePosition();
        });
        resolveLiveComponent(popover).then((component: Component | null): void => {
            this.liveComponent = component;
        });
        this.dispatch('opened');
        if (moveFocusInside && this.isDropdownBehaviour()) {
            this.moveFocusInside(popover);
        }
    }

    /**
     * Click mode hides at once; hover mode waits briefly so the pointer can move from the trigger into the
     * popover.
     */
    public hide(): Promise<void> {
        this.clearHideTimeout();
        if (!this.isPopoverVisible) {
            return Promise.resolve();
        }
        if (this.isDropdownBehaviour()) {
            this.hideImmediately();
            return Promise.resolve();
        }
        return new Promise((resolve): void => {
            this.hideTimeout = setTimeout((): void => {
                this.hideTimeout = null;
                if (!this.isPopoverFocused) {
                    this.hideImmediately();
                }
                resolve();
            }, hoverHideDelay);
        });
    }

    public onGlobalClick(event: Event): void {
        if (!this.isPopoverVisible || this.dataElement === null) {
            return;
        }
        if (isInsideOverlayTree(event.target, [this.element, this.dataElement])) {
            return;
        }
        this.hideImmediately();
    }

    public handleKeydown(event: KeyboardEvent): void {
        if (event.key !== 'Escape' || event.defaultPrevented || !this.isPopoverVisible || !overlayStack.isTopmost(this)) {
            return;
        }
        // Marks the key press as handled, so the overlay below does not close on the same press.
        event.preventDefault();
        this.clearHideTimeout();
        this.hideImmediately(true);
    }

    public async computePosition(): Promise<void> {
        const popover = this.dataElement;
        if (!this.isPopoverVisible || popover === null) {
            return;
        }
        if (!this.element.isConnected || !isElementVisible(this.element)) {
            this.hideImmediately();
            return;
        }
        const middleware = [offset(4), flip(), shift({padding: 5})];
        const arrowElement = popover.querySelector<HTMLElement>('[data-poppover-caret]');
        if (arrowElement !== null) {
            middleware.push(arrow({element: arrowElement, padding: 6}));
        }
        const position = await computePosition(this.element, popover, {
            placement: (this.hasPlacementValue && this.placementValue !== '' ? this.placementValue : 'top') as Placement,
            strategy: 'fixed',
            middleware: middleware,
        });
        if (!this.isPopoverVisible || this.dataElement !== popover) {
            return;
        }
        popover.style.left = `${position.x}px`;
        popover.style.top = `${position.y}px`;
        if (arrowElement === null) {
            return;
        }
        // The caret classes add the caret padding; the template starts with the bottom caret, so the first
        // measurement of the default `top` placement already includes it.
        if (position.placement.startsWith('top')) {
            popover.classList.add(this.caretBottomClass);
            popover.classList.remove(this.caretTopClass);
        } else if (position.placement.startsWith('bottom')) {
            popover.classList.add(this.caretTopClass);
            popover.classList.remove(this.caretBottomClass);
        } else {
            // A side placement has no caret: it is drawn only for top and bottom.
            popover.classList.remove(this.caretTopClass, this.caretBottomClass);
        }
        const arrowPosition = position.middlewareData.arrow;
        if (arrowPosition === undefined) {
            return;
        }
        arrowElement.style.left = typeof arrowPosition.x === 'number' ? `${arrowPosition.x}px` : '';
    }

    private isDropdownBehaviour(): boolean {
        return this.hasDropdownBehaviourValue && this.dropdownBehaviourValue;
    }

    /**
     * Announces the dialog on a click trigger (unless the markup sets its own `aria-haspopup`) and its state.
     */
    private syncTriggerAria(): void {
        if (!this.element.hasAttribute('aria-haspopup')) {
            this.element.setAttribute('aria-haspopup', 'dialog');
        }
        this.element.setAttribute('aria-expanded', this.isPopoverVisible ? 'true' : 'false');
    }

    private moveFocusInside(popover: HTMLElement): void {
        const target = findInitialFocus(popover);
        if (target !== null) {
            focusWithoutScroll(target);
            return;
        }
        if (!popover.hasAttribute('tabindex')) {
            popover.tabIndex = -1;
        }
        focusWithoutScroll(popover);
    }

    /**
     * Hiding a click popover loses the focus inside it, so the focus goes back to the trigger; `returnFocus`
     * also returns a focus that is already lost (closed by keyboard or from a close-on-click element).
     * `afterAction` marks a close after the Live action of a close-on-click element (`popover:closed` detail).
     */
    private hideImmediately(returnFocus: boolean = false, afterAction: boolean = false): void {
        if (!this.isPopoverVisible) {
            return;
        }
        const focusTrigger = this.isDropdownBehaviour() && this.dataElement !== null
            && (returnFocus ? isFocusInsideOrLost(this.dataElement) : isFocusInside(this.dataElement));
        this.dispatch('closing');
        this.isPopoverVisible = false;
        this.isPopoverFocused = false;
        overlayStack.unregister(this);
        this.stopPositionUpdates();
        if (this.isDropdownBehaviour()) {
            this.element.setAttribute('aria-expanded', 'false');
        }
        if (this.describedById !== null) {
            removeIdReference(this.describedElement ?? this.element, 'aria-describedby', this.describedById);
            this.describedById = null;
            this.describedElement = null;
        }
        this.element.classList.remove(this.isOpenClass);
        const popover = this.dataElement;
        if (popover !== null) {
            if (isPopoverOpen(popover)) {
                popover.hidePopover();
            }
            popover.style.visibility = '';
            popover.setAttribute('aria-hidden', 'true');
            popover.classList.remove(this.isOpenClass);
            if (this.hasRemoveOnHideValue && this.removeOnHideValue) {
                this.destroyDataElement();
            }
        }
        if (focusTrigger && this.element.isConnected) {
            focusWithoutScroll(this.element);
        }
        const detail: OverlayClosedDetail = {afterAction: afterAction};
        this.dispatch('closed', {detail: detail});
    }

    private hideAfterClick(event: Event): void {
        const popover = this.dataElement;
        if (popover === null) {
            return;
        }
        const marked = findCloseOnClickElement(event.target, popover, closeOnClickAttribute);
        if (marked === null) {
            return;
        }
        const component = this.liveComponent;
        if (component !== null && triggersLiveAction(event.target, marked)) {
            // Keeps the button (and its loader) visible until the Live request ends.
            const openCount = this.openCount;
            waitForLiveRequest(component).then((): void => {
                if (openCount === this.openCount) {
                    this.hideImmediately(true, true);
                }
            });
            return;
        }
        setTimeout((): void => this.hideImmediately(true));
    }

    private clearHideTimeout(): void {
        if (this.hideTimeout !== null) {
            clearTimeout(this.hideTimeout);
            this.hideTimeout = null;
        }
    }

    private stopPositionUpdates(): void {
        if (this.stopAutoUpdate !== null) {
            this.stopAutoUpdate();
            this.stopAutoUpdate = null;
        }
    }

    /**
     * The element a hover popover describes: the trigger when it is focusable itself, otherwise the focused
     * element inside it, or its first focusable element (a wrapper around a button); the trigger as a fallback.
     */
    private findDescribedElement(): HTMLElement {
        if (this.element.tabIndex >= 0) {
            return this.element;
        }
        const active = document.activeElement;
        if (active instanceof HTMLElement && active !== this.element && this.element.contains(active)) {
            return active;
        }
        return findInitialFocus(this.element) ?? this.element;
    }

    private destroyDataElement(): void {
        this.dataElementListeners?.abort();
        this.dataElementListeners = null;
        this.liveComponent = null;
        if (this.dataElement !== null) {
            this.dataElement.remove();
            this.dataElement = null;
        }
    }

    private getDataElement(): HTMLElement {
        if (this.dataElement !== null) {
            return this.dataElement;
        }
        const element = createHTMLElementFromTemplate(this.templateTarget);
        const listeners = new AbortController();
        const signal = listeners.signal;
        element.setAttribute('aria-hidden', 'true');
        // The trigger announces `aria-haspopup="dialog"`; the panel template does not know the mode.
        if (!element.hasAttribute('role')) {
            element.setAttribute('role', this.isDropdownBehaviour() ? 'dialog' : 'tooltip');
        }
        element.addEventListener('mouseenter', (): void => {
            this.isPopoverFocused = true;
        }, {signal});
        element.addEventListener('focusin', (): void => {
            this.isPopoverFocused = true;
        }, {signal});
        const onLeave = async (): Promise<void> => {
            this.isPopoverFocused = false;
            if (!this.isDropdownBehaviour()) {
                await this.hide();
            }
        };
        element.addEventListener('mouseleave', onLeave, {signal});
        element.addEventListener('focusout', (event: FocusEvent): void => {
            if (event.relatedTarget instanceof Node && element.contains(event.relatedTarget)) {
                return;
            }
            onLeave();
        }, {signal});
        // Capture phase: a `:stop` modifier on the clicked Live action must not prevent the close.
        element.addEventListener('click', (event: MouseEvent): void => this.hideAfterClick(event), {capture: true, signal});
        closeWithNestedOverlayAfterAction(element, (): void => {
            this.clearHideTimeout();
            this.hideImmediately(true, true);
        }, signal);
        // Keeps the state in sync when something else hides the native popover.
        element.addEventListener('toggle', (event: Event): void => {
            if (isToggleCloseEvent(event) && this.isPopoverVisible && this.dataElement === element) {
                this.hideImmediately();
            }
        }, {signal});
        if (this.hasCloseOnEventsValue && this.closeOnEventsValue.length > 0) {
            for (const eventName of this.closeOnEventsValue) {
                addEventListener(element, eventName, (): void => {
                    setTimeout((): void => this.hideImmediately());
                }, {signal});
            }
        }
        appendToRoot((this.hasRootValue ? this.rootValue : 'body') as Root, element, this.element);
        setPanelOwner(element, this.element);
        this.dataElement = element;
        this.dataElementListeners = listeners;
        return element;
    }
}
